import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { API } from "@upclaim/sdk";

import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

export const passengerRequestsKey = (claimId: string) =>
  [...queryKeys.claims.detail(claimId), "passenger-requests"] as const;

/** Every ask on the claim, across all of its acquisitions. */
export function usePassengerRequests(claimId: string) {
  return useQuery({
    queryKey: passengerRequestsKey(claimId),
    queryFn: async () => (await adminAPI.listClaimPassengerRequests(claimId)).requests,
  });
}

/** Saves the batch as a draft — nothing reaches the passenger until "Envoyer la demande". */
export function useCreatePassengerRequests(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      familyClaimAcquisitionId,
      requests,
    }: {
      familyClaimAcquisitionId: string;
      requests: API.CreatePassengerRequestItem[];
    }) =>
      notify.promise(adminAPI.createPassengerRequests(familyClaimAcquisitionId, { requests }), {
        loading: "Enregistrement des demandes…",
        success: "Demandes enregistrées — rien n'a encore été envoyé",
        analytics: { action: "create-passenger-requests" },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: passengerRequestsKey(claimId) }),
  });
}

/** First send and relaunch are the same endpoint — only `notifiedAt` tells them apart. */
export function useNotifyPassengerRequests(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      familyClaimAcquisitionId,
      isRelaunch,
    }: {
      familyClaimAcquisitionId: string;
      isRelaunch: boolean;
    }) =>
      notify.promise(adminAPI.notifyPassengerRequests(familyClaimAcquisitionId), {
        loading: isRelaunch ? "Relance en cours…" : "Envoi de la demande…",
        success: isRelaunch ? "Passager relancé" : "Demande envoyée au passager",
        analytics: { action: "notify-passenger-requests" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: passengerRequestsKey(claimId) });
      // Notifying blocks the claim, so the claim itself is stale too.
      queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}

export function useCancelPassengerRequest(claimId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (passengerRequestId: string) =>
      notify.promise(adminAPI.cancelPassengerRequest(passengerRequestId), {
        loading: "Annulation…",
        success: "Demande annulée",
        analytics: { action: "cancel-passenger-request" },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: passengerRequestsKey(claimId) });
      // Cancelling the last required ask can unblock the claim.
      queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
    },
  });
}
